import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { clsx } from 'clsx';
import {
  Zap, Calendar, Globe, Webhook, Users, Server, HardDrive,
  Shield, Ticket, Bell, GitBranch, RefreshCw, Layers,
  Clock, CheckSquare, Bot, Settings, Play,
  MessageSquare, Mail, Activity,
} from 'lucide-react';

export interface OrbayxNodeData {
  label: string;
  nodeType: string;
  integration?: string;
  action?: string;
  description?: string;
  params?: Record<string, unknown>;
  isSelected?: boolean;
}

const NODE_CONFIGS: Record<string, {
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  border: string;
  bg: string;
  category: string;
}> = {
  // Triggers
  trigger_manual: { icon: Play, color: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', category: 'Trigger' },
  trigger_schedule: { icon: Calendar, color: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', category: 'Trigger' },
  trigger_event: { icon: Zap, color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', category: 'Trigger' },
  trigger_api: { icon: Globe, color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/10', category: 'Trigger' },
  trigger_webhook: { icon: Webhook, color: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/10', category: 'Trigger' },

  // AD Actions
  action_ad: { icon: Users, color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/10', category: 'Action' },

  // VM Actions
  action_vm: { icon: Server, color: 'text-purple-400', border: 'border-purple-500/30', bg: 'bg-purple-500/10', category: 'Action' },

  // Storage Actions
  action_storage: { icon: HardDrive, color: 'text-orange-400', border: 'border-orange-500/30', bg: 'bg-orange-500/10', category: 'Action' },

  // Security Actions
  action_security: { icon: Shield, color: 'text-red-400', border: 'border-red-500/30', bg: 'bg-red-500/10', category: 'Action' },

  // Notification Actions
  action_notification: { icon: Bell, color: 'text-yellow-400', border: 'border-yellow-500/30', bg: 'bg-yellow-500/10', category: 'Action' },

  // ITSM Actions
  action_itsm: { icon: Ticket, color: 'text-cyan-400', border: 'border-cyan-500/30', bg: 'bg-cyan-500/10', category: 'Action' },

  // Controls
  control_condition: { icon: GitBranch, color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10', category: 'Control' },
  control_loop: { icon: RefreshCw, color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10', category: 'Control' },
  control_parallel: { icon: Layers, color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10', category: 'Control' },
  control_wait: { icon: Clock, color: 'text-amber-400', border: 'border-amber-500/30', bg: 'bg-amber-500/10', category: 'Control' },
  control_approval: { icon: CheckSquare, color: 'text-indigo-400', border: 'border-indigo-500/30', bg: 'bg-indigo-500/10', category: 'Control' },

  // Integrations
  integration_servicenow: { icon: Ticket, color: 'text-emerald-400', border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', category: 'Integration' },
  integration_slack: { icon: MessageSquare, color: 'text-pink-400', border: 'border-pink-500/30', bg: 'bg-pink-500/10', category: 'Integration' },
  integration_teams: { icon: MessageSquare, color: 'text-blue-400', border: 'border-blue-500/30', bg: 'bg-blue-500/10', category: 'Integration' },
  integration_email: { icon: Mail, color: 'text-gray-400', border: 'border-gray-500/30', bg: 'bg-gray-500/10', category: 'Integration' },

  // AI
  ai_analyze: { icon: Bot, color: 'text-violet-400', border: 'border-violet-500/30', bg: 'bg-violet-500/10', category: 'AI' },
  ai_decide: { icon: Bot, color: 'text-violet-400', border: 'border-violet-500/30', bg: 'bg-violet-500/10', category: 'AI' },
  ai_generate: { icon: Bot, color: 'text-violet-400', border: 'border-violet-500/30', bg: 'bg-violet-500/10', category: 'AI' },
};

const DEFAULT_NODE_CONFIG = { icon: Settings, color: 'text-gray-400', border: 'border-gray-500/30', bg: 'bg-gray-500/10', category: 'Node' };

export function OrbayxNode({ data, selected }: NodeProps<OrbayxNodeData>) {
  const nodeType = data.nodeType || 'action_ad';
  const config = NODE_CONFIGS[nodeType] || DEFAULT_NODE_CONFIG;
  const Icon = config.icon;
  const isTrigger = nodeType.startsWith('trigger_');
  const isAI = nodeType.startsWith('ai_');

  return (
    <div
      className={clsx(
        'min-w-[160px] max-w-[200px] rounded-xl border-2 transition-all duration-150',
        config.border,
        selected ? 'ring-2 ring-orbayx-500/50 shadow-glow' : '',
        config.bg,
        'backdrop-blur-sm',
      )}
    >
      {/* Target Handle (top) */}
      {!isTrigger && (
        <Handle
          type="target"
          position={Position.Left}
          className="!w-3 !h-3 !bg-white/20 !border-2 !border-gray-200 hover:!border-orbayx-400 transition-colors"
        />
      )}

      <div className="p-3">
        {/* Category badge */}
        <div className={clsx('text-[9px] font-semibold uppercase tracking-widest mb-1.5 flex items-center gap-1', config.color)}>
          {isAI && <span className="w-1 h-1 rounded-full bg-violet-400 animate-pulse" />}
          {config.category}
        </div>

        {/* Icon + Label */}
        <div className="flex items-start gap-2">
          <div className={clsx('p-1.5 rounded-lg flex-shrink-0', config.bg)}>
            <Icon className={clsx('w-3.5 h-3.5', config.color)} />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-white leading-tight line-clamp-2">{data.label}</div>
            {(data.integration || data.description) && (
              <div className="text-[10px] text-gray-400 mt-0.5 leading-tight truncate">
                {data.description || data.integration}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Source Handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !bg-white/20 !border-2 !border-gray-200 hover:!border-orbayx-400 transition-colors"
      />

      {/* Conditional handles for condition nodes */}
      {nodeType === 'control_condition' && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            style={{ left: '35%' }}
            className="!w-3 !h-3 !bg-red-500/40 !border-2 !border-red-400/60 hover:!border-red-400"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            style={{ left: '65%' }}
            className="!w-3 !h-3 !bg-emerald-500/40 !border-2 !border-emerald-400/60 hover:!border-emerald-400"
          />
        </>
      )}
    </div>
  );
}
